import { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo, type ReactNode } from "react";
import { supabase } from "@/lib/supabase";
import type { GlobalTheme } from "@/hooks/useThemeData";

export interface DarkThemeSettings {
  darkModeEnabled: boolean;
  autoDarkMode: boolean;
  darkPrimaryColor: string;
  darkSecondaryColor: string;
  darkAccentColor: string;
  darkBodyBg: string;
  darkFooterBg: string;
}

const DEFAULT_THEME: GlobalTheme = {
  primaryColor: "#0D9488",
  secondaryColor: "#14B8A6",
  accentColor: "#F97316",
  fontHeading: "Playfair Display",
  fontBody: "Inter",
  siteLogoUrl: "https://storage.readdy-site.link/project_files/2c592e0a-ac16-4b41-a5a3-753cedde2d0c/822e93fe-7f15-40fb-a3ea-010140fb5a30_isman-falcon-figma2.png?v=92077e07eca8bef5c11686db6fc155dd",
  borderRadius: "8",
  spacingScale: "1",
  buttonStyle: "rounded",
  heroBgImage: "",
  footerBgColor: "#F5F5F5",
  bodyBgColor: "#e4f9f9",
};

const DEFAULT_DARK: DarkThemeSettings = {
  darkModeEnabled: false,
  autoDarkMode: false,
  darkPrimaryColor: "#111827",
  darkSecondaryColor: "#374151",
  darkAccentColor: "#F97316",
  darkBodyBg: "#0F172A",
  darkFooterBg: "#1E293B",
};

export interface ThemeContextType {
  theme: GlobalTheme;
  pageOverrides: Record<string, string>;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  setPageId: (pageId: string | null) => void;
  darkMode: boolean;
  setDarkMode: (v: boolean) => void;
  autoDarkMode: boolean;
  darkTheme: DarkThemeSettings;
}

const ThemeContext = createContext<ThemeContextType>({
  theme: DEFAULT_THEME,
  pageOverrides: {},
  loading: false,
  error: null,
  refresh: async () => {},
  setPageId: () => {},
  darkMode: false,
  setDarkMode: () => {},
  autoDarkMode: false,
  darkTheme: DEFAULT_DARK,
});

export function useTheme() {
  return useContext(ThemeContext);
}

function parseSettings(rows: Array<Record<string, unknown>>): GlobalTheme {
  const map = new Map<string, string>();
  for (const row of rows) {
    if (row.key && row.value !== undefined) {
      map.set(String(row.key), String(row.value));
    }
  }
  const get = (key: string, fallback: string) => map.get(key) || fallback;
  return {
    primaryColor: get("primary_color", DEFAULT_THEME.primaryColor),
    secondaryColor: get("secondary_color", DEFAULT_THEME.secondaryColor),
    accentColor: get("accent_color", DEFAULT_THEME.accentColor),
    fontHeading: get("font_heading", DEFAULT_THEME.fontHeading),
    fontBody: get("font_body", DEFAULT_THEME.fontBody),
    siteLogoUrl: get("site_logo_url", DEFAULT_THEME.siteLogoUrl),
    borderRadius: get("border_radius", DEFAULT_THEME.borderRadius),
    spacingScale: get("spacing_scale", DEFAULT_THEME.spacingScale),
    buttonStyle: get("button_style", DEFAULT_THEME.buttonStyle),
    heroBgImage: get("hero_bg_image", DEFAULT_THEME.heroBgImage),
    footerBgColor: get("footer_bg_color", DEFAULT_THEME.footerBgColor),
    bodyBgColor: get("body_bg_color", DEFAULT_THEME.bodyBgColor),
  };
}

function parseDarkSettings(rows: Array<Record<string, unknown>>): DarkThemeSettings {
  const map = new Map<string, string>();
  for (const row of rows) {
    if (row.key && row.value !== undefined) {
      map.set(String(row.key), String(row.value));
    }
  }
  const get = (key: string, fallback: string) => map.get(key) || fallback;
  const getBool = (key: string) => {
    const v = map.get(key);
    return v === "true" || v === "1";
  };
  return {
    darkModeEnabled: getBool("dark_mode_enabled"),
    autoDarkMode: getBool("auto_dark_mode"),
    darkPrimaryColor: get("dark_primary_color", DEFAULT_DARK.darkPrimaryColor),
    darkSecondaryColor: get("dark_secondary_color", DEFAULT_DARK.darkSecondaryColor),
    darkAccentColor: get("dark_accent_color", DEFAULT_DARK.darkAccentColor),
    darkBodyBg: get("dark_body_bg", DEFAULT_DARK.darkBodyBg),
    darkFooterBg: get("dark_footer_bg", DEFAULT_DARK.darkFooterBg),
  };
}

const hexToRgbCache = new Map<string, string>();
function hexToRgb(hex: string): string {
  const cached = hexToRgbCache.get(hex);
  if (cached) return cached;
  const cleaned = hex.replace("#", "");
  const bigint = parseInt(cleaned, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  const result = `${r} ${g} ${b}`;
  hexToRgbCache.set(hex, result);
  return result;
}

function isNightTime(): boolean {
  const hour = new Date().getHours();
  return hour >= 18 || hour < 6;
}

let currentInjectedTheme: string | null = null;

function injectCSSVars(
  theme: GlobalTheme,
  overrides: Record<string, string>,
  isDark: boolean,
  darkSettings: DarkThemeSettings
) {
  const t = { ...theme, ...overrides };
  const themeKey = JSON.stringify({ t, isDark, darkSettings });
  if (themeKey === currentInjectedTheme) return;
  currentInjectedTheme = themeKey;

  const root = document.documentElement;

  if (isDark) {
    root.style.setProperty("--brand-primary", darkSettings.darkPrimaryColor);
    root.style.setProperty("--brand-secondary", darkSettings.darkSecondaryColor);
    root.style.setProperty("--brand-accent", darkSettings.darkAccentColor);
    root.style.setProperty("--brand-primary-rgb", hexToRgb(darkSettings.darkPrimaryColor));
    root.style.setProperty("--brand-secondary-rgb", hexToRgb(darkSettings.darkSecondaryColor));
    root.style.setProperty("--brand-accent-rgb", hexToRgb(darkSettings.darkAccentColor));
    root.style.setProperty("--brand-footer-bg", darkSettings.darkFooterBg);
    root.style.setProperty("--brand-body-bg", darkSettings.darkBodyBg);
    document.body.style.backgroundColor = darkSettings.darkBodyBg;
    root.classList.add("dark");
  } else {
    root.style.setProperty("--brand-primary", t.primaryColor);
    root.style.setProperty("--brand-secondary", t.secondaryColor);
    root.style.setProperty("--brand-accent", t.accentColor);
    root.style.setProperty("--brand-primary-rgb", hexToRgb(t.primaryColor));
    root.style.setProperty("--brand-secondary-rgb", hexToRgb(t.secondaryColor));
    root.style.setProperty("--brand-accent-rgb", hexToRgb(t.accentColor));
    root.style.setProperty("--brand-footer-bg", t.footerBgColor);
    root.style.setProperty("--brand-body-bg", t.bodyBgColor);
    if (t.bodyBgColor) {
      document.body.style.backgroundColor = t.bodyBgColor;
    }
    root.classList.remove("dark");
  }

  root.style.setProperty("--brand-font-heading", `"${t.fontHeading}"`);
  root.style.setProperty("--brand-font-body", `"${t.fontBody}"`);
  root.style.setProperty("--brand-logo-url", `url(${t.siteLogoUrl})`);
  root.style.setProperty("--brand-radius", `${t.borderRadius}px`);
  root.style.setProperty("--brand-spacing", t.spacingScale);
  root.style.setProperty("--brand-button-style", t.buttonStyle);
}

function getSessionId(): string {
  let id = localStorage.getItem("ab_session_id");
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem("ab_session_id", id);
  }
  return id;
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<GlobalTheme>(DEFAULT_THEME);
  const [darkTheme, setDarkTheme] = useState<DarkThemeSettings>(DEFAULT_DARK);
  const [pageOverrides, setPageOverrides] = useState<Record<string, string>>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pageId, setPageIdState] = useState<string | null>(null);
  const [darkMode, setDarkMode] = useState(false);
  const [abVariantTheme, setAbVariantTheme] = useState<Record<string, string> | null>(null);

  const pageIdRef = useRef(pageId);
  pageIdRef.current = pageId;

  const shouldBeDark = useMemo(() => {
    const userPref = localStorage.getItem("dark_mode_user");
    if (userPref === "true") return true;
    if (userPref === "false") return false;
    if (darkTheme.autoDarkMode) return isNightTime();
    if (darkTheme.darkModeEnabled) return true;
    return false;
  }, [darkTheme]);

  const fetchTheme = useCallback(async () => {
    try {
      const currentPid = pageIdRef.current;
      const [settingsRes, pageRes] = await Promise.all([
        supabase.from("site_settings").select("key, value"),
        currentPid
          ? supabase.from("page_themes").select("theme_json").eq("page_id", currentPid).maybeSingle()
          : Promise.resolve({ data: null, error: null }),
      ]);

      if (settingsRes.error) throw settingsRes.error;

      const rows = (settingsRes.data ?? []) as Array<Record<string, unknown>>;
      const parsed = parseSettings(rows);
      const darkParsed = parseDarkSettings(rows);
      setTheme(parsed);
      setDarkTheme(darkParsed);

      const userPref = localStorage.getItem("dark_mode_user");
      let isDarkNow = false;
      if (userPref === "true") isDarkNow = true;
      else if (userPref === "false") isDarkNow = false;
      else if (darkParsed.autoDarkMode) isDarkNow = isNightTime();
      else if (darkParsed.darkModeEnabled) isDarkNow = true;
      setDarkMode(isDarkNow);

      const pageJson = pageRes.data?.theme_json as Record<string, string> | undefined;
      setPageOverrides(pageJson ?? {});

      injectCSSVars(parsed, { ...(pageJson ?? {}), ...(abVariantTheme ?? {}) }, isDarkNow, darkParsed);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Theme load failed");
      injectCSSVars(DEFAULT_THEME, {}, false, DEFAULT_DARK);
    } finally {
      setLoading(false);
    }
  }, [abVariantTheme]);

  const checkScheduledThemes = useCallback(async () => {
    try {
      const res = await fetch("/functions/v1/schedule-theme/execute", {
        method: "POST",
        headers: { apikey: import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY },
      });
      if (res.ok) {
        const json = await res.json();
        if (json.executed > 0) {
          const settingsRes = await supabase.from("site_settings").select("key, value");
          const parsed = parseSettings((settingsRes.data ?? []) as Array<Record<string, unknown>>);
          setTheme(parsed);
          injectCSSVars(
            parsed,
            pageOverrides,
            darkMode,
            darkTheme
          );
        }
      }
    } catch {
      // silent fail
    }
  }, [pageOverrides, darkMode, darkTheme]);

  const checkABExperiment = useCallback(async () => {
    const currentPath = window.location.pathname;
    const pagePath = currentPath.replace(/^\//, "").split("/")[0] || "home";

    try {
      const { data: experiments } = await supabase
        .from("ab_experiments")
        .select("*")
        .eq("status", "running")
        .or(`target_page.eq.${pagePath},target_page.is.null`)
        .maybeSingle();

      if (!experiments) {
        setAbVariantTheme(null);
        return;
      }

      const sessionId = getSessionId();
      const res = await fetch("/functions/v1/ab-experiment/assign", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apikey: import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({ experimentId: experiments.id, sessionId }),
      });

      if (!res.ok) {
        setAbVariantTheme(null);
        return;
      }

      const { themeJson } = await res.json();
      if (themeJson && Object.keys(themeJson).length > 0) {
        setAbVariantTheme(themeJson);
      } else {
        setAbVariantTheme(null);
      }

      fetch("/functions/v1/ab-experiment/event", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apikey: import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({
          experimentId: experiments.id,
          sessionId,
          eventType: "view",
          pagePath: currentPath,
        }),
      }).catch(() => {});
    } catch {
      setAbVariantTheme(null);
    }
  }, []);

  // Track engagement
  useEffect(() => {
    const currentPath = window.location.pathname;
    const pagePath = currentPath.replace(/^\//, "").split("/")[0] || "home";

    const handleClick = () => {
      trackABEvent(pagePath, "click");
    };

    const handleScroll = () => {
      const scrollPercent = (window.scrollY / (document.body.scrollHeight - window.innerHeight)) * 100;
      if (scrollPercent > 50) {
        trackABEvent(pagePath, "scroll", { depth: Math.round(scrollPercent) });
      }
    };

    document.addEventListener("click", handleClick);
    window.addEventListener("scroll", handleScroll, { passive: true });

    const startTime = Date.now();
    const timer = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startTime) / 1000);
      if (elapsed > 30) {
        trackABEvent(pagePath, "time_on_page", { seconds: elapsed });
      }
    }, 30000);

    trackThemeAnalytics(pagePath, 0);

    const handleBeforeUnload = () => {
      const elapsed = Math.floor((Date.now() - startTime) / 1000);
      trackThemeAnalytics(pagePath, elapsed);
    };
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      document.removeEventListener("click", handleClick);
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("beforeunload", handleBeforeUnload);
      clearInterval(timer);
    };
  }, [pageId]);

  const trackABEvent = useCallback(async (pagePath: string, eventType: string, metadata?: Record<string, unknown>) => {
    const sessionId = getSessionId();
    try {
      const { data: experiments } = await supabase
        .from("ab_experiments")
        .select("id")
        .eq("status", "running")
        .or(`target_page.eq.${pagePath},target_page.is.null`)
        .maybeSingle();

      if (!experiments) return;

      await fetch("/functions/v1/ab-experiment/event", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apikey: import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({
          experimentId: experiments.id,
          sessionId,
          eventType,
          pagePath,
          metadata,
        }),
      });
    } catch {
      // silent fail
    }
  }, []);

  const trackThemeAnalytics = useCallback(async (pagePath: string, duration: number) => {
    const sessionId = getSessionId();
    const device = /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent) ? "mobile" : "desktop";
    try {
      await fetch("/functions/v1/theme-analytics/track", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apikey: import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({
          pageId: pagePath,
          device,
          sessionId,
          duration,
          events: { view: 1 },
        }),
      });
    } catch {
      // silent fail
    }
  }, []);

  // Initial load + realtime subscriptions
  useEffect(() => {
    setLoading(true);
    fetchTheme();

    const channel = supabase
      .channel("theme_changes_v4")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "site_settings" },
        () => fetchTheme()
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "page_themes" },
        () => fetchTheme()
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [fetchTheme]);

  // Auto dark mode timer
  useEffect(() => {
    if (!darkTheme.autoDarkMode) return;
    const timer = setInterval(() => {
      const shouldDark = isNightTime();
      const userPref = localStorage.getItem("dark_mode_user");
      if (userPref === null && shouldDark !== darkMode) {
        setDarkMode(shouldDark);
        injectCSSVars(theme, pageOverrides, shouldDark, darkTheme);
      }
    }, 60000);
    return () => clearInterval(timer);
  }, [darkTheme.autoDarkMode, darkMode, theme, pageOverrides, darkTheme]);

  const setPageId = useCallback((id: string | null) => {
    setPageIdState(id);
  }, []);

  const handleSetDarkMode = useCallback((v: boolean) => {
    setDarkMode(v);
    localStorage.setItem("dark_mode_user", String(v));
    injectCSSVars(theme, pageOverrides, v, darkTheme);
  }, [theme, pageOverrides, darkTheme]);

  const refresh = useCallback(async () => {
    setLoading(true);
    await fetchTheme();
  }, [fetchTheme]);

  return (
    <ThemeContext.Provider
      value={{
        theme,
        pageOverrides,
        loading,
        error,
        refresh,
        setPageId,
        darkMode,
        setDarkMode: handleSetDarkMode,
        autoDarkMode: darkTheme.autoDarkMode,
        darkTheme,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
}